// src/components/EventDashboard.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  CardMedia,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Skeleton,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import {
  collection,
  query,
  where,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import dayjs from 'dayjs';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Sidebar from './Sidebar';
import { Delete, MoreVert, Edit } from '@mui/icons-material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ImageIcon from '@mui/icons-material/Image';
import IosShareIcon from '@mui/icons-material/IosShare';

// ---- helpers: image safety ----
const isValidImgSrc = (u) =>
  typeof u === 'string' &&
  (u.startsWith('http') ||
    u.startsWith('data:') ||
    u.startsWith('blob:'));

const fallbackCover =
  'https://dummyimage.com/1200x800/eaeaea/999999.jpg&text=Add+a+cover+photo';

// ✅ IG-style action tile hover/press styles (Event / Share / QR)
const actionItemSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '14px',
  paddingTop: '10px',
  paddingBottom: '10px',
  cursor: 'pointer',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  transition: 'background-color 140ms ease, transform 120ms ease',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
  '&:active': {
    backgroundColor: 'rgba(0,0,0,0.06)',
    transform: 'scale(0.96)',
  },
  '&:focus-visible': {
    outline: '2px solid rgba(0,0,0,0.25)',
    outlineOffset: '2px',
  },
};

const actionIconBtnSx = {
  mb: 0.5,
  color: '#000',
  '&:hover': { backgroundColor: 'transparent' },
};

const actionLabelSx = {
  fontSize: 13,
  fontWeight: 600,
  color: '#000',
};

const EventDashboard = () => {
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [slowLoading, setSlowLoading] = useState(false);
  const [user, setUser] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [deleting, setDeleting] = useState(false);

  const [visibilityToast, setVisibilityToast] = useState({
    open: false,
    message: '',
  });

  const navigate = useNavigate();
  const auth = getAuth();
  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const newEventId = params.get("newEventId");

  // Only clear drafts after Stripe's successful return
  if (!newEventId) return;

  try {
    sessionStorage.removeItem("pricingCompleted");
    sessionStorage.removeItem("openPricingOnStep3");
    sessionStorage.removeItem("reservedEventId");
    sessionStorage.removeItem("draftEventDetails");
    sessionStorage.removeItem("draftCustomization");
  } catch {}

  // Remove newEventId from the URL without refreshing the dashboard
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete("newEventId");

  window.history.replaceState(
    {},
    "",
    `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`
  );
}, []);

  const unsubscribeEventsRef = useRef(null);
  const retryTimerRef = useRef(null);
  const listenerWatchdogRef = useRef(null);
  const hardStopTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const currentUserIdRef = useRef(null);
  const hasLoadedEventsRef = useRef(false);

  const haptic = useCallback((kind = 'light') => {
    try {
      if (!('vibrate' in navigator)) return;
      navigator.vibrate(kind === 'heavy' ? 20 : 10);
    } catch (_) {}
  }, []);

  const handleMenuOpen = (e, selectedEventArg) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedEvent(selectedEventArg);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleDeleteClick = (ev) => {
    if (!ev) return;

    setEventToDelete(ev);
    setDialogOpen(true);
    handleMenuClose();
    haptic('light');
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete || deleting) return;

    setDeleting(true);

    try {
      await deleteDoc(doc(db, 'events', eventToDelete.id));

      setEvents((prev) =>
        prev.filter((ev) => ev.id !== eventToDelete.id)
      );

      console.log('Event deleted successfully');
      haptic('heavy');
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setDeleting(false);
      setDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    if (deleting) return;

    setDialogOpen(false);
    setEventToDelete(null);
  };

  const handleEditDetails = () => {
    if (!selectedEvent) return;

    navigate(`/edit-event/${selectedEvent.id}/step1`);
    handleMenuClose();
  };

  const handleEditCustomization = () => {
    if (!selectedEvent) return;

    navigate(`/edit-event/${selectedEvent.id}/step2`);
    handleMenuClose();
  };

  const handleRetrieveQRCode = () => {
    if (!selectedEvent) return;

    haptic('light');

    try {
      sessionStorage.setItem(
        `elevate:qrEventData:${selectedEvent.id}`,
        JSON.stringify(selectedEvent)
      );
    } catch (_) {}

    navigate(`/eventpreview/${selectedEvent.id}`);
    handleMenuClose();
  };

  const clearLoadingTimers = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    if (listenerWatchdogRef.current) {
      clearTimeout(listenerWatchdogRef.current);
      listenerWatchdogRef.current = null;
    }

    if (hardStopTimerRef.current) {
      clearTimeout(hardStopTimerRef.current);
      hardStopTimerRef.current = null;
    }
  };

  const stopEventsListener = () => {
    if (unsubscribeEventsRef.current) {
      unsubscribeEventsRef.current();
      unsubscribeEventsRef.current = null;
    }
  };

  const startHardStopTimer = () => {
    if (hardStopTimerRef.current) {
      clearTimeout(hardStopTimerRef.current);
    }

    // Prevent the dashboard from remaining stuck indefinitely.
    hardStopTimerRef.current = setTimeout(() => {
      if (hasLoadedEventsRef.current) return;

      setLoading(false);
      setLoadError(true);
    }, 25000);
  };

  const scheduleEventsRetry = (userId, immediate = false) => {
    if (!userId || currentUserIdRef.current !== userId) return;

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }

    const delay = immediate
      ? 0
      : Math.min(
          1000 * 2 ** retryAttemptRef.current,
          15000
        );

    if (!immediate) {
      retryAttemptRef.current += 1;
    }

    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;

      if (currentUserIdRef.current !== userId) return;

      stopEventsListener();
      unsubscribeEventsRef.current = fetchEvents(userId);
    }, delay);
  };

  const retryEventsNow = () => {
    const userId =
      currentUserIdRef.current || auth.currentUser?.uid;

    if (!userId) {
      window.location.reload();
      return;
    }

    currentUserIdRef.current = userId;
    retryAttemptRef.current = 0;

    setLoading(true);
    setLoadError(false);
    setSlowLoading(false);

    startHardStopTimer();
    scheduleEventsRetry(userId, true);
  };

  useEffect(() => {
    startHardStopTimer();

    const unsubscribeAuth = onAuthStateChanged(
      auth,
      (currentUser) => {
        stopEventsListener();

        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }

        retryAttemptRef.current = 0;
        hasLoadedEventsRef.current = false;

        setLoadError(false);
        setSlowLoading(false);

        if (currentUser) {
          currentUserIdRef.current = currentUser.uid;

          setUser(currentUser);
          setLoading(true);

          startHardStopTimer();

          unsubscribeEventsRef.current = fetchEvents(
            currentUser.uid
          );
        } else {
          currentUserIdRef.current = null;

          setUser(null);
          setEvents([]);
          setLoading(false);

          if (hardStopTimerRef.current) {
            clearTimeout(hardStopTimerRef.current);
            hardStopTimerRef.current = null;
          }
        }
      },
      (error) => {
        console.error(
          'auth state listener error:',
          error
        );

        setLoading(false);
        setLoadError(true);
      }
    );

    const handleOnline = () => {
      const userId = currentUserIdRef.current;

      if (!userId) return;

      setSlowLoading(false);
      scheduleEventsRetry(userId, true);
    };

    const handleOffline = () => {
      if (!hasLoadedEventsRef.current) {
        setSlowLoading(true);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      stopEventsListener();
      clearLoadingTimers();

      currentUserIdRef.current = null;

      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      unsubscribeAuth();
    };
  }, [auth]);

  const fetchEvents = (userId) => {
    const eventsRef = collection(db, 'events');

    const q = query(
      eventsRef,
      where('userId', '==', userId)
    );

    let firstResultReceived = false;

    if (listenerWatchdogRef.current) {
      clearTimeout(listenerWatchdogRef.current);
    }

    // Restart Firestore silently if it hangs without success or error.
    listenerWatchdogRef.current = setTimeout(() => {
      if (
        firstResultReceived ||
        currentUserIdRef.current !== userId
      ) {
        return;
      }

      setSlowLoading(true);

      stopEventsListener();
      scheduleEventsRetry(userId);
    }, 8000);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        if (currentUserIdRef.current !== userId) {
          return;
        }

        firstResultReceived = true;

        if (listenerWatchdogRef.current) {
          clearTimeout(listenerWatchdogRef.current);
          listenerWatchdogRef.current = null;
        }

        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }

        if (hardStopTimerRef.current) {
          clearTimeout(hardStopTimerRef.current);
          hardStopTimerRef.current = null;
        }

        retryAttemptRef.current = 0;
        hasLoadedEventsRef.current = true;

        const eventsList = querySnapshot.docs.map(
          (docRef) => {
            const data = docRef.data() || {};
            const details = data.details || {};
            const customization =
              data.customization || {};

            const mapped = {
              id: docRef.id,
              ...data,
              eventName:
                details.eventName || 'Untitled Event',
              eventDate:
                details.eventDate || null,
              welcomeMessage:
                details.welcomeMessage || '',
              coverPhotoUrl:
                (typeof customization?.coverPhotoUrl ===
                'string'
                  ? customization.coverPhotoUrl
                  : '') ||
                (typeof data?.coverPhotoUrl === 'string'
                  ? data.coverPhotoUrl
                  : '') ||
                '',
              uniqueUrl:
                data.uniqueUrl ||
                data.slug ||
                data.id ||
                docRef.id,
            };

            return mapped;
          }
        );

        eventsList.sort((a, b) => {
          const da = a.eventDate
            ? new Date(a.eventDate).getTime()
            : Infinity;

          const dbb = b.eventDate
            ? new Date(b.eventDate).getTime()
            : Infinity;

          return da - dbb;
        });

        setEvents(eventsList);
        setLoading(false);
        setLoadError(false);
        setSlowLoading(false);
      },
      (err) => {
        if (currentUserIdRef.current !== userId) {
          return;
        }

        firstResultReceived = true;

        if (listenerWatchdogRef.current) {
          clearTimeout(listenerWatchdogRef.current);
          listenerWatchdogRef.current = null;
        }

        console.error(
          'user events index listener error:',
          err
        );

        // Keep existing cards visible if a later listener fails.
        if (!hasLoadedEventsRef.current) {
          setSlowLoading(true);
        }

        scheduleEventsRetry(userId);
      }
    );

    return unsubscribe;
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'events', id));

      setEvents((prev) =>
        prev.filter((event) => event.id !== id)
      );

      console.log('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const now = dayjs();

  const upcomingEvents = events.filter(
    (event) =>
      !event?.eventDate ||
      dayjs(event.eventDate).isAfter(now)
  );

  const completedEvents = events.filter((event) =>
    event?.eventDate
      ? dayjs(event.eventDate).isBefore(now)
      : false
  );

  if (
    loading ||
    (loadError && events.length === 0)
  ) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
        }}
        aria-busy={loading}
      >
        <Sidebar />

        <Container
          sx={{
            padding: {
              xs: '16px',
              sm: '20px',
            },
            background:
              'linear-gradient(to bottom, #f0f4f8, #ffffff)',
            boxShadow:
              '0px 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            marginTop: {
              xs: '16px',
              sm: '20px',
            },
            marginBottom: {
              xs: '16px',
              sm: '20px',
            },
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <Skeleton
            variant="rounded"
            width={220}
            height={66}
            sx={{
              mb: 3,
              borderRadius: '12px',
            }}
          />

          {(slowLoading || loadError) && (
            <Alert
              severity={
                navigator.onLine
                  ? 'info'
                  : 'warning'
              }
              action={
                loadError ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryEventsNow}
                  >
                    Retry
                  </Button>
                ) : null
              }
              sx={{
                mb: 2,
                borderRadius: '12px',
              }}
            >
              {navigator.onLine
                ? loadError
                  ? 'We could not load your events yet. We are still retrying automatically.'
                  : 'This is taking longer than usual. We are retrying automatically.'
                : 'You appear to be offline. We will reconnect automatically.'}
            </Alert>
          )}

          <Skeleton
            variant="text"
            width={180}
            height={38}
            sx={{
              mx: 'auto',
              mb: 1,
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              overflow: 'hidden',
              padding: '10px 0',
            }}
          >
            {[0, 1, 2].map((item) => (
              <Card
                key={item}
                sx={{
                  width: '350px',
                  flexShrink: 0,
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  height={240}
                />

                <CardContent
                  sx={{ padding: '24px' }}
                >
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={32}
                  />

                  <Skeleton
                    variant="text"
                    width="42%"
                    height={24}
                  />

                  <Skeleton
                    variant="rounded"
                    width={130}
                    height={28}
                    sx={{
                      mt: 1,
                      borderRadius: '999px',
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      height={58}
                      sx={{ flex: 1 }}
                    />

                    <Skeleton
                      variant="rounded"
                      height={58}
                      sx={{ flex: 1 }}
                    />

                    <Skeleton
                      variant="rounded"
                      height={58}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </div>
    );
  }

  const CreateNewEventButton = () => (
    <Box
      component={Link}
      to="/create-event"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: '#fff',
        border:
          '1px solid rgba(0,0,0,0.07)',
        borderRadius: '12px',
        boxShadow:
          '0 4px 12px rgba(0,0,0,0.04)',
        padding: {
          xs: '12px 16px',
          sm: '14px 16px',
        },
        cursor: 'pointer',
        textDecoration: 'none',
        width: {
          xs: '100%',
          sm: 'auto',
        },
        maxWidth: {
          xs: '100%',
          sm: 360,
        },
        touchAction: 'manipulation',
        WebkitTapHighlightColor:
          'transparent',
        transition:
          'transform 120ms ease, box-shadow 180ms ease, background-color 180ms ease',
        '&:hover': {
          boxShadow:
            '0 6px 16px rgba(0,0,0,0.08)',
          backgroundColor: '#fafafa',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
        '&:focus-visible': {
          outline:
            '2px solid rgba(0,0,0,0.25)',
          outlineOffset: '3px',
          borderRadius: '14px',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#f8f8f8',
          border:
            '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '20px',
          fontWeight: 500,
          color: '#111',
          lineHeight: 1,
        }}
      >
        +
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1.2,
          textAlign: {
            xs: 'left',
            sm: 'left',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: '15px',
              sm: '15px',
            },
            fontWeight: 600,
            color: '#111',
          }}
        >
          Create New Event
        </Typography>

        <Typography
          sx={{
            fontSize: {
              xs: '13px',
              sm: '12px',
            },
            fontWeight: 400,
            color: '#777',
          }}
        >
          Get your QR code in minutes
        </Typography>
      </Box>
    </Box>
  );

  const handleShareEvent = (event) => {
    const shareUrl =
      `${window.location.origin}/events/${event.id}`;

    const shareData = {
      title:
        event.eventName || 'Elevate Event',
      text:
        'Upload your photos to our shared gallery ✨',
      url: shareUrl,
    };

    haptic('light');

    if (navigator.share) {
      navigator
        .share(shareData)
        .catch((err) =>
          console.log(
            'Share cancelled or failed:',
            err
          )
        );
    } else if (
      navigator.clipboard &&
      navigator.clipboard.writeText
    ) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() =>
          alert(
            'Event link copied to clipboard'
          )
        )
        .catch((err) =>
          console.log(
            'Clipboard copy failed:',
            err
          )
        );
    } else {
      alert(shareUrl);
    }
  };

  const handleToggleGalleryVisibility =
    async (event) => {
      if (!event?.id) return;

      const currentlyVisible =
        event.galleryVisibleToGuests !== false;

      const nextVisible =
        !currentlyVisible;

      try {
        haptic('light');

        await updateDoc(
          doc(db, 'events', event.id),
          {
            galleryVisibleToGuests:
              nextVisible,
          }
        );

        setVisibilityToast({
          open: true,
          message: nextVisible
            ? 'Gallery is visible to guests again.'
            : 'Gallery hidden from guests. You can still see it as the host.',
        });
      } catch (error) {
        console.error(
          'Error updating gallery visibility:',
          error
        );

        alert(
          'Could not update gallery visibility. Please try again.'
        );
      }
    };

  const EventActionsRow = ({ event }) => {
    const goEvent = () => {
      haptic('light');

      try {
        sessionStorage.setItem(
          `elevate:eventData:${event.id}`,
          JSON.stringify(event)
        );

        if (event.uniqueUrl) {
          sessionStorage.setItem(
            `elevate:eventData:${event.uniqueUrl}`,
            JSON.stringify(event)
          );
        }
      } catch (_) {}

      navigate(`/events/${event.id}`);
    };

    const goQR = () => {
      haptic('light');

      try {
        sessionStorage.setItem(
          `elevate:qrEventData:${event.id}`,
          JSON.stringify(event)
        );
      } catch (_) {}

      navigate(
        `/eventpreview/${event.id}`
      );
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent:
            'space-between',
          gap: 0.5,
          mt: 1,
          pt: 1,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label="Open event"
          sx={actionItemSx}
          onClick={goEvent}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' ||
              e.key === ' '
            ) {
              e.preventDefault();
              goEvent();
            }
          }}
        >
          <IconButton
            sx={actionIconBtnSx}
            disableRipple
          >
            <ImageIcon />
          </IconButton>

          <Typography sx={actionLabelSx}>
            Event
          </Typography>
        </Box>

        <Box
          role="button"
          tabIndex={0}
          aria-label="Share event"
          sx={actionItemSx}
          onClick={() =>
            handleShareEvent(event)
          }
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' ||
              e.key === ' '
            ) {
              e.preventDefault();
              handleShareEvent(event);
            }
          }}
        >
          <IconButton
            sx={actionIconBtnSx}
            disableRipple
          >
            <IosShareIcon />
          </IconButton>

          <Typography sx={actionLabelSx}>
            Share
          </Typography>
        </Box>

        <Box
          role="button"
          tabIndex={0}
          aria-label="Open QR code"
          sx={actionItemSx}
          onClick={goQR}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' ||
              e.key === ' '
            ) {
              e.preventDefault();
              goQR();
            }
          }}
        >
          <IconButton
            sx={actionIconBtnSx}
            disableRipple
          >
            <QrCode2Icon />
          </IconButton>

          <Typography sx={actionLabelSx}>
            QR
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Sidebar />

      <Container
        sx={{
          padding: {
            xs: '16px',
            sm: '20px',
          },
          background:
            'linear-gradient(to bottom, #f0f4f8, #ffffff)',
          boxShadow:
            '0px 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          marginTop: {
            xs: '16px',
            sm: '20px',
          },
          marginBottom: {
            xs: '16px',
            sm: '20px',
          },
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        {upcomingEvents.length === 0 &&
          completedEvents.length === 0 && (
            <Box
              sx={{
                textAlign: {
                  xs: 'left',
                  sm: 'center',
                },
                mb: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: {
                  xs: 'stretch',
                  sm: 'center',
                },
                gap: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#000',
                  fontSize: {
                    xs: '18px',
                    sm: '20px',
                  },
                }}
              >
                Welcome 👋
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#555',
                  maxWidth: 360,
                  fontSize: {
                    xs: '14px',
                    sm: '15px',
                  },
                }}
              >
                Start your first event to
                collect every guest’s photos
                in one shared gallery.
              </Typography>

              <CreateNewEventButton />
            </Box>
          )}

        {(upcomingEvents.length > 0 ||
          completedEvents.length > 0) && (
          <Box
            sx={{
              mb: '24px',
              maxWidth: {
                xs: '100%',
                sm: '360px',
              },
            }}
          >
            <CreateNewEventButton />
          </Box>
        )}

        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: '600',
            marginBottom: '10px',
            textAlign: 'center',
            fontSize: {
              xs: '17px',
              sm: '20px',
            },
          }}
        >
          Upcoming Events
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            padding: '10px 0',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling:
              'touch',
          }}
        >
          {upcomingEvents.length > 0 ? (
            upcomingEvents.map((event) => (
              <Card
                key={event.id}
                sx={{
                  width: '350px',
                  height: 'auto',
                  flexShrink: 0,
                  borderRadius: '16px',
                  boxShadow:
                    '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition:
                    'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform:
                      'translateY(-5px)',
                    boxShadow:
                      '0 8px 16px rgba(0, 0, 0, 0.15)',
                  },
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <IconButton
                  onClick={(e) =>
                    handleMenuOpen(e, event)
                  }
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: '#000',
                    backgroundColor:
                      '#fff',
                    borderRadius: '50%',
                    padding: '8px',
                    boxShadow:
                      '0px 2px 4px rgba(0, 0, 0, 0.2)',
                    zIndex: 2,
                    '&:hover': {
                      backgroundColor:
                        '#f0f0f0',
                    },
                    '&:active': {
                      transform:
                        'scale(0.96)',
                    },
                    '& svg': {
                      fontSize: '24px',
                    },
                  }}
                >
                  <MoreVert />
                </IconButton>

                <CardMedia
                  component="img"
                  height="240"
                  image={
                    isValidImgSrc(
                      event.coverPhotoUrl
                    )
                      ? event.coverPhotoUrl
                      : fallbackCover
                  }
                  alt={event.eventName}
                  sx={{
                    borderRadius:
                      '16px 16px 0 0',
                    objectFit: 'cover',
                    objectPosition:
                      '50% 30%',
                  }}
                />

                <CardContent
                  sx={{ padding: '24px' }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 'bold',
                      marginBottom:
                        '12px',
                      fontSize: {
                        xs: '16px',
                        sm: '18px',
                      },
                      wordBreak:
                        'break-word',
                    }}
                  >
                    {event.eventName}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      marginBottom:
                        '8px',
                      fontSize: {
                        xs: '14px',
                        sm: '14px',
                      },
                    }}
                  >
                    {event.eventDate
                      ? dayjs(
                          event.eventDate
                        ).format(
                          'MMMM D, YYYY'
                        )
                      : 'No date'}
                  </Typography>

                  <Chip
                    clickable
                    onClick={() =>
                      handleToggleGalleryVisibility(
                        event
                      )
                    }
                    size="small"
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems:
                            'center',
                          gap: 0.5,
                        }}
                      >
                        <span>
                          {event.galleryVisibleToGuests ===
                          false
                            ? 'Hidden from guests'
                            : 'Visible to guests'}
                        </span>

                        <span
                          style={{
                            fontSize:
                              '14px',
                            lineHeight: 1,
                          }}
                        >
                          ›
                        </span>
                      </Box>
                    }
                    sx={{
                      mb: '12px',
                      height: 28,
                      borderRadius:
                        '999px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition:
                        'all 140ms ease',
                      backgroundColor:
                        event.galleryVisibleToGuests ===
                        false
                          ? 'rgba(0,0,0,0.08)'
                          : 'rgba(46,125,50,0.10)',
                      color:
                        event.galleryVisibleToGuests ===
                        false
                          ? '#333'
                          : '#2e7d32',
                      '&:hover': {
                        transform:
                          'scale(1.03)',
                      },
                      '&:active': {
                        transform:
                          'scale(0.97)',
                      },
                    }}
                  />

                  <EventActionsRow
                    event={event}
                  />
                </CardContent>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 3,
                    style: {
                      minWidth:
                        '220px',
                    },
                  }}
                >
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditDetails();
                    }}
                  >
                    <Edit
                      fontSize="small"
                      sx={{
                        marginRight:
                          '8px',
                      }}
                    />

                    Edit Event Details
                  </MenuItem>

                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetrieveQRCode();
                    }}
                  >
                    <QrCode2Icon
                      fontSize="small"
                      sx={{
                        marginRight:
                          '8px',
                      }}
                    />

                    Get QR code
                  </MenuItem>

                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(
                        selectedEvent
                      );
                    }}
                  >
                    <Delete
                      fontSize="small"
                      sx={{
                        marginRight:
                          '8px',
                        color: '#d32f2f',
                      }}
                    />

                    Delete Event
                  </MenuItem>
                </Menu>
              </Card>
            ))
          ) : (
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{
                display: 'flex',
                justifyContent:
                  'center',
                alignItems: 'center',
                height: '200px',
                textAlign: 'center',
                fontSize: {
                  xs: '14px',
                  sm: '15px',
                },
              }}
            >
              Your events will appear here.
            </Typography>
          )}
        </Box>

        {completedEvents.length > 0 && (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: '600',
                marginTop: '40px',
                marginBottom: '10px',
                fontSize: {
                  xs: '17px',
                  sm: '20px',
                },
              }}
            >
              Completed Events
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: '16px',
                overflowX: 'auto',
                padding: '10px 0',
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling:
                  'touch',
              }}
            >
              {completedEvents.map(
                (event) => (
                  <Card
                    key={event.id}
                    sx={{
                      width: '350px',
                      height: 'auto',
                      flexShrink: 0,
                      borderRadius:
                        '16px',
                      boxShadow:
                        '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transition:
                        'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform:
                          'translateY(-5px)',
                        boxShadow:
                          '0 8px 16px rgba(0, 0, 0, 0.15)',
                      },
                      position:
                        'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <IconButton
                      onClick={(e) =>
                        handleMenuOpen(
                          e,
                          event
                        )
                      }
                      sx={{
                        position:
                          'absolute',
                        top: 8,
                        right: 8,
                        color: '#000',
                        backgroundColor:
                          '#fff',
                        borderRadius:
                          '50%',
                        padding: '8px',
                        boxShadow:
                          '0px 2px 4px rgba(0, 0, 0, 0.2)',
                        zIndex: 2,
                        '&:hover': {
                          backgroundColor:
                            '#f0f0f0',
                        },
                        '&:active': {
                          transform:
                            'scale(0.96)',
                        },
                        '& svg': {
                          fontSize:
                            '24px',
                        },
                      }}
                    >
                      <MoreVert />
                    </IconButton>

                    <CardMedia
                      component="img"
                      height="240"
                      image={
                        isValidImgSrc(
                          event.coverPhotoUrl
                        )
                          ? event.coverPhotoUrl
                          : fallbackCover
                      }
                      alt={event.eventName}
                      sx={{
                        borderRadius:
                          '16px 16px 0 0',
                        objectFit: 'cover',
                        objectPosition:
                          '50% 30%',
                      }}
                    />

                    <CardContent
                      sx={{
                        padding: '24px',
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight:
                            'bold',
                          marginBottom:
                            '12px',
                          fontSize: {
                            xs: '16px',
                            sm: '18px',
                          },
                          wordBreak:
                            'break-word',
                        }}
                      >
                        {event.eventName}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          marginBottom:
                            '8px',
                          fontSize: {
                            xs: '14px',
                            sm: '14px',
                          },
                        }}
                      >
                        {event.eventDate
                          ? dayjs(
                              event.eventDate
                            ).format(
                              'MMMM D, YYYY'
                            )
                          : 'No date'}
                      </Typography>

                      <Chip
                        clickable
                        onClick={() =>
                          handleToggleGalleryVisibility(
                            event
                          )
                        }
                        size="small"
                        label={
                          <Box
                            sx={{
                              display:
                                'flex',
                              alignItems:
                                'center',
                              gap: 0.5,
                            }}
                          >
                            <span>
                              {event.galleryVisibleToGuests ===
                              false
                                ? 'Hidden from guests'
                                : 'Visible to guests'}
                            </span>

                            <span
                              style={{
                                fontSize:
                                  '14px',
                                lineHeight:
                                  1,
                              }}
                            >
                              ›
                            </span>
                          </Box>
                        }
                        sx={{
                          mb: '12px',
                          height: 28,
                          borderRadius:
                            '999px',
                          fontSize:
                            '12px',
                          fontWeight: 600,
                          cursor:
                            'pointer',
                          transition:
                            'all 140ms ease',
                          backgroundColor:
                            event.galleryVisibleToGuests ===
                            false
                              ? 'rgba(0,0,0,0.08)'
                              : 'rgba(46,125,50,0.10)',
                          color:
                            event.galleryVisibleToGuests ===
                            false
                              ? '#333'
                              : '#2e7d32',
                          '&:hover': {
                            transform:
                              'scale(1.03)',
                          },
                          '&:active': {
                            transform:
                              'scale(0.97)',
                          },
                        }}
                      />

                      <EventActionsRow
                        event={event}
                      />
                    </CardContent>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(
                        anchorEl
                      )}
                      onClose={
                        handleMenuClose
                      }
                      PaperProps={{
                        elevation: 3,
                        style: {
                          minWidth:
                            '220px',
                        },
                      }}
                    >
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDetails();
                        }}
                      >
                        <Edit
                          fontSize="small"
                          sx={{
                            marginRight:
                              '8px',
                          }}
                        />

                        Edit Event Details
                      </MenuItem>

                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRetrieveQRCode();
                        }}
                      >
                        <QrCode2Icon
                          fontSize="small"
                          sx={{
                            marginRight:
                              '8px',
                          }}
                        />

                        Get QR code
                      </MenuItem>

                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(
                            selectedEvent
                          );
                        }}
                      >
                        <Delete
                          fontSize="small"
                          sx={{
                            marginRight:
                              '8px',
                            color:
                              '#d32f2f',
                          }}
                        />

                        Delete Event
                      </MenuItem>
                    </Menu>
                  </Card>
                )
              )}
            </Box>
          </>
        )}
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <DialogTitle id="confirm-delete-title">
          Are you sure?
        </DialogTitle>

        <DialogContent>
          <Typography id="confirm-delete-description">
            Deleting this event will remove
            it permanently, and this action
            cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            color="primary"
            disabled={deleting}
          >
            No, Cancel
          </Button>

          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleting}
            sx={{ minWidth: 110 }}
          >
            {deleting
              ? 'Deleting…'
              : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={visibilityToast.open}
        autoHideDuration={3500}
        onClose={() =>
          setVisibilityToast((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            borderRadius: '999px',
            fontWeight: 600,
            alignItems: 'center',
          }}
        >
          {visibilityToast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EventDashboard;